import React, { useState, useEffect, createContext, useContext } from "react";
import axios from "axios";
import { Button, EmptyState, Spinner, Tabs, TextInput } from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../../shared/toastHelper";
import { useTemplateAutosave } from "../../../shared/useTemplateAutosave";

import { useBeefreeTemplateBlueprintEditorContext } from "./BeefreeTemplateBlueprintEditorContext";
import TemplateEditor from "../BeefreeTemplates/TemplateEditor";
import ErrorsContainer from "../BeefreeTemplates/ErrorsContainer";
import SendTestOverlay from "../BeefreeTemplates/SendTestOverlay";
import AdminBlueprintPreviewTab from "./AdminBlueprintPreviewTab";

export const BlueprintComposerTemplatableContext = createContext();

export function useBlueprintComposerTemplatableContext() {
  return useContext(BlueprintComposerTemplatableContext);
}

const composerContainerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh",
  display: "flex",
  backgroundColor: "var(--sl-bg-secondary)",
  color: "var(--sl-text-primary)",
  padding: 24,
  gap: 12,
  flexDirection: "column",
};

const rowStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: 12,
};

const nameRowStyle = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  minWidth: 280,
};

const placeholderStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "50vh",
};

const BlueprintComposerTemplatableProvider = ({ children, template, setTemplate, processedTemplate, setProcessedTemplate, errors, setErrors, beefreeTemplate, user, testModalOpen, setTestModalOpen, csrfToken, team, beefreeToken, sampleData }) => {
  const value = {
    template,
    setTemplate,
    processedTemplate,
    setProcessedTemplate,
    errors,
    setErrors,
    sampleData: sampleData || {},
    isEmailTemplate: true,
    beefreeTemplate,
    beefreeTemplateId: beefreeTemplate?.id,
    user,
    testModalOpen,
    setTestModalOpen,
    subject: "",
    preheader: "",
    csrfToken,
    team,
    beefreeToken,
    venueOwnership: null,
    isAdminBlueprint: true,
  };

  return (
    <BlueprintComposerTemplatableContext.Provider value={value}>
      {children}
    </BlueprintComposerTemplatableContext.Provider>
  );
};

export default () => {
  const {
    csrfToken,
    team,
    beefreeTemplateBlueprint,
    setBeefreeTemplateBlueprint,
    user,
    beefreeToken,
    beefreeTemplateBlueprintBaseUrl,
    setShowComposer,
  } = useBeefreeTemplateBlueprintEditorContext();

  const [tab, setTab] = useState("Content");
  const [template, setTemplate] = useState(beefreeTemplateBlueprint?.template || "");
  const [processedTemplate, setProcessedTemplate] = useState(template);
  const [name, setName] = useState(beefreeTemplateBlueprint?.name || "");
  const [errors, setErrors] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);

  useEffect(() => {
    const templateChanged = processedTemplate !== beefreeTemplateBlueprint?.template;
    const nameChanged = name !== beefreeTemplateBlueprint?.name;
    setSaveDisabled(!templateChanged && !nameChanged);
  }, [processedTemplate, name, beefreeTemplateBlueprint?.template, beefreeTemplateBlueprint?.name]);

  useEffect(() => {
    if (beefreeTemplateBlueprint?.template) {
      setTemplate(beefreeTemplateBlueprint.template);
      setProcessedTemplate(beefreeTemplateBlueprint.template);
    }
    if (beefreeTemplateBlueprint?.name) {
      setName(beefreeTemplateBlueprint.name);
    }
  }, [beefreeTemplateBlueprint]);

  const updateBlueprint = () => {
    // Kill any queued/in-flight autosave first so it can't land after this
    // manual write and revert it.
    cancelAutosave();
    setIsLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(beefreeTemplateBlueprintBaseUrl, {
      beefree_template_blueprint: {
        template: processedTemplate,
        name: name,
      },
    })
      .then(({ data }) => {
        setBeefreeTemplateBlueprint(data);
        setTemplate(data.template);
        setProcessedTemplate(data.template);
        setName(data.name);
        setIsLoading(false);
        setSaveDisabled(true);
        toastSuccess("Email blueprint updated successfully.");
      })
      .catch(() => {
        toastError("Unable to update email blueprint.");
        setIsLoading(false);
      });
  };

  // Silent autosave, mirroring the manual Save payload but template-only (name
  // stays manual) and WITHOUT setBeefreeTemplateBlueprint on the response, so a
  // background PATCH can't stomp in-flight edits. The blueprint always exists
  // (found by id, with a template initialized on create), so the id guard here
  // is belt-and-suspenders.
  const { isAutosaving, autosave: autosaveTemplate, cancel: cancelAutosave } = useTemplateAutosave((templateData, cancelToken) => {
    if (!beefreeTemplateBlueprint?.id) return Promise.resolve();

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.patch(beefreeTemplateBlueprintBaseUrl, {
      beefree_template_blueprint: { template: templateData },
    }, { cancelToken });
  });

  return (
    <BlueprintComposerTemplatableProvider
      template={template}
      setTemplate={setTemplate}
      processedTemplate={processedTemplate}
      setProcessedTemplate={setProcessedTemplate}
      errors={errors}
      setErrors={setErrors}
      beefreeTemplate={beefreeTemplateBlueprint}
      user={user}
      testModalOpen={testModalOpen}
      setTestModalOpen={setTestModalOpen}
      csrfToken={csrfToken}
      team={team}
      beefreeToken={beefreeToken}
      sampleData={beefreeTemplateBlueprint.sample_data}
    >
      <SendTestOverlay templatableContext={useBlueprintComposerTemplatableContext} />
      <div style={composerContainerStyle}>
        <div style={rowStyle}>
          <div style={nameRowStyle}>
            <div style={{ flex: 1 }}>
              <TextInput
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Blueprint name"
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Button variant="link-color" as="a" href="/admin/beefree_templates_admin_blueprints">
              Back to admin
            </Button>
            {template ? (
              <Button variant="primary" disabled={saveDisabled || isLoading || isAutosaving} onClick={updateBlueprint}>
                {isAutosaving ? "Saving…" : "Save"}
              </Button>
            ) : null}
          </div>
        </div>
        {template ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={rowStyle}>
              <Tabs
                items={[{ value: "Content", label: "Content" }, { value: "Preview", label: "Preview" }]}
                value={tab}
                onChange={setTab}
                collapseBelow={0}
              />
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <ErrorsContainer templatableContext={useBlueprintComposerTemplatableContext} />
                <Button variant="secondary" onClick={() => setTestModalOpen(true)}>
                  Send Test
                </Button>
              </div>
            </div>
            {tab === "Content" ? (
              <div style={{ boxShadow: "var(--sl-shadow-sm)" }}>
                <TemplateEditor
                  templatableContext={useBlueprintComposerTemplatableContext}
                  autosave
                  onAutosave={autosaveTemplate}
                  mergeTags={[]}
                />
              </div>
            ) : tab === "Preview" ? (
              <AdminBlueprintPreviewTab
                template={template}
                sampleData={beefreeTemplateBlueprint.sample_data || {}}
                beefreeTemplateId={beefreeTemplateBlueprint.id}
                csrfToken={csrfToken}
              />
            ) : null}
          </div>
        ) : isLoading ? (
          <div style={placeholderStyle}>
            <Spinner aria-label="Loading" />
          </div>
        ) : (
          <div style={placeholderStyle}>
            <EmptyState title="No template found" description="Please create a template to get started." />
          </div>
        )}
      </div>
    </BlueprintComposerTemplatableProvider>
  );
};
